import {Policy} from '../types-repository';

/**
 * What a principal's policies allow, as the UI needs to know it: which controls to show and which
 * `input.*` values to offer. The authorizer (`auth_components/lambdas/appsync_auth`) is the actual gate, so
 * every question here is a hint and errs towards showing a control rather than hiding one.
 *
 * - A **deny** statement beats an allow statement for the action and resource it matches.
 * - A statement with **conditions** cannot be decided here: they test request arguments the UI does not have yet.
 *   An allow with conditions counts as allowed; a deny with conditions does not block.
 * - Policies that are not loaded yet (`null`) allow nothing. Nothing throws.
 */

type Policies = readonly Policy[] | null | undefined;

/** fnmatch-style: `*` any run, `?` one character, as the authorizer matches resources. */
export function globMatch(pattern: string, value: string): boolean {
  const regex = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp(`^${regex}$`).test(value);
}

function statementsFor(policies: Policies, action: string, effect: 'allow' | 'deny') {
  return (policies ?? [])
    .flatMap(policy => policy.statements ?? [])
    .filter(statement => statement.effect === effect && statement.actions?.includes(action));
}

const hasConditions = (statement: {conditions?: readonly unknown[] | null}) => !!statement.conditions?.length;

/**
 * Whether `action` is allowed on `resource`. A null `resource` asks "on anything at all": whether the principal
 * holds the action anywhere, which only an unconditional deny of every resource (`*`) can withdraw.
 */
export function isGranted(policies: Policies, action: string, resource: string | null): boolean {
  const covers = (resources: readonly string[] | null | undefined, wholly: boolean) =>
    (resources ?? []).some(pattern => wholly ? pattern === '*' : globMatch(pattern, resource!));
  const denied = statementsFor(policies, action, 'deny')
    .some(statement => !hasConditions(statement) && covers(statement.resources, resource === null));
  if (denied) {
    return false;
  }
  const allows = statementsFor(policies, action, 'allow');
  return resource === null ? allows.length > 0 : allows.some(statement => covers(statement.resources, false));
}

/**
 * The values a principal may send for one request argument, as the patterns its statements grant for `action`:
 * the part after `resourcePrefix` of every matching resource (so `contentplatform:pool:input.pool/above_above`
 * with the prefix `contentplatform:pool:input.pool/` yields `above_above`), plus `*` for a resource of `*`.
 * `allow` and `deny` come back separately because a deny can only be applied to a concrete value.
 */
export function grantedPatterns(policies: Policies, action: string, resourcePrefix: string):
  {allow: string[]; deny: string[]} {
  const patterns = (effect: 'allow' | 'deny') => statementsFor(policies, action, effect)
    .filter(statement => effect === 'allow' || !hasConditions(statement))
    .flatMap(statement => statement.resources ?? [])
    .flatMap(resource => resource === '*' ? ['*']
      : resource.startsWith(resourcePrefix) ? [resource.slice(resourcePrefix.length)] : []);
  return {allow: patterns('allow'), deny: patterns('deny')};
}

/** Whether `value` is granted by one of `grant`'s allow patterns and by none of its deny patterns. */
export function grantsValue(grant: {allow: readonly string[]; deny: readonly string[]}, value: string): boolean {
  return grant.allow.some(pattern => globMatch(pattern, value))
    && !grant.deny.some(pattern => globMatch(pattern, value));
}

/** The first granted allow pattern that no deny pattern covers, or null when there is none. */
export function firstGrantedPattern(grant: {allow: readonly string[]; deny: readonly string[]}): string | null {
  return grant.allow.find(pattern => !grant.deny.some(deny => globMatch(deny, pattern))) ?? null;
}
